import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Brain, BadgeCheck, Award, ShieldCheck } from "lucide-react";

const Gauge = ({ iq }) => {
  const pct = Math.max(0, Math.min(1, (iq - 70) / 80));
  const R = 70, C = 2 * Math.PI * R;
  return (
    <div className="relative w-44 h-44">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
        <circle cx="80" cy="80" r={R} fill="none" stroke="url(#vg)" strokeWidth="12" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)} />
        <defs><linearGradient id="vg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#06B6D4" /><stop offset="100%" stopColor="#6366F1" /></linearGradient></defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-sub font-black text-4xl text-white">{iq}</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">IQ Score</span>
      </div>
    </div>
  );
};

export const Verify = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/public/result/${shareId}`).then((res) => { setData(res.data); setStatus("ok"); }).catch(() => setStatus("error"));
  }, [shareId]);

  return (
    <div className="min-h-screen bg-[#0B0F17] grain relative">
      <div className="absolute top-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <nav className="relative z-10 max-w-5xl mx-auto px-6 h-20 flex items-center">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center"><Brain className="w-5 h-5 text-white" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">The Cognitive <span className="text-cyan-400">Metrics</span></span>
        </Link>
      </nav>

      <div className="relative z-10 max-w-3xl mx-auto px-5 py-10">
        {status === "loading" && <div className="text-center text-slate-400 font-mono-data text-sm py-20">Verifying certificate…</div>}
        {status === "error" && (
          <div className="text-center py-24">
            <p className="text-xl font-semibold text-white">Certificate not found</p>
            <p className="text-slate-400 mt-2">This verification link is invalid or has been revoked.</p>
            <Link to="/" className="inline-block mt-5 text-cyan-400 hover:underline">Take the assessment →</Link>
          </div>
        )}
        {status === "ok" && data && (
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 mb-5">
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Verified result</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{data.name}</h1>
            <p className="text-slate-400 mt-2">completed the credited 30-item intelligence assessment on {new Date(data.created_at).toLocaleDateString()}.</p>

            <div className="mt-8 grid sm:grid-cols-[auto,1fr] gap-7 items-center p-7 rounded-3xl bg-[#131B2A] border border-white/10">
              <div className="flex justify-center"><Gauge iq={data.iq} /></div>
              <div>
                <div className="text-lg font-semibold text-cyan-300">{data.band}</div>
                <div className="text-sm text-slate-400 mt-1">Smarter than {data.percentile}% of test-takers · {data.correct}/{data.total} correct</div>
                <div className="mt-5 space-y-3">
                  {data.breakdown.map((b) => (
                    <div key={b.key}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-300">{b.label}</span>
                        <span className="font-mono-data text-cyan-300">{b.percent}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-full" style={{ width: `${b.percent}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-center gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Authenticity verified</span>
              <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-amber-400" /> The Cognitive Metrics</span>
            </div>

            <div className="text-center mt-8">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all">
                Take the assessment yourself
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
